<script setup lang="ts">
import PublicLayout from '@/components/PublicLayout.vue'
</script>

<template>
  <PublicLayout>
    <div class="max-w-4xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-6 sm:py-8">

        <!-- Header -->
        <div class="mb-6 sm:mb-8">
          <h1 class="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-800 mb-2">Privacy Policy</h1>
          <p class="text-xs sm:text-sm text-gray-600">Last updated: January 2025</p>
        </div>

        <!-- Content -->
        <div class="prose prose-sm max-w-none space-y-6">
          <section>
            <h2 class="text-2xl font-semibold text-gray-800 mb-3">1. Introduction</h2>
            <p class="text-gray-700 leading-relaxed">
              PT Karunia Solusi Informatika ("KSI Digital," "we," "us," or "our") is committed to protecting your privacy.
              This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use AutomaDoc Pro,
              our cloud-based document automation service that integrates with Lark (Feishu).
            </p>
          </section>

          <section>
            <h2 class="text-2xl font-semibold text-gray-800 mb-3">2. Information We Collect</h2>
            <h3 class="text-xl font-semibold text-gray-800 mb-2 mt-4">2.1 Information You Provide</h3>
            <ul class="list-disc pl-6 space-y-2 text-gray-700">
              <li><strong>Account Information:</strong> Lark tenant key, user IDs, and base tokens</li>
              <li><strong>Templates:</strong> Document templates you upload to the service</li>
              <li><strong>Generated Documents:</strong> Documents created using your templates and Lark Base data</li>
              <li><strong>Configuration Data:</strong> Settings, field mappings, and preferences</li>
            </ul>

            <h3 class="text-xl font-semibold text-gray-800 mb-2 mt-4">2.2 Information Automatically Collected</h3>
            <ul class="list-disc pl-6 space-y-2 text-gray-700">
              <li><strong>Usage Data:</strong> Number of documents generated, template usage, and feature interactions</li>
              <li><strong>Technical Data:</strong> IP addresses, browser type, device information, and access times</li>
              <li><strong>Performance Data:</strong> Service uptime, error logs, and system diagnostics</li>
            </ul>
          </section>

          <section>
            <h2 class="text-2xl font-semibold text-gray-800 mb-3">3. How We Use Your Information</h2>
            <p class="text-gray-700 mb-3">We use your information to:</p>
            <ul class="list-disc pl-6 space-y-2 text-gray-700">
              <li>Provide and maintain the AutomaDoc Pro service</li>
              <li>Generate documents from your templates and Lark Base data</li>
              <li>Process your requests and respond to your inquiries</li>
              <li>Monitor service usage and enforce quota limits</li>
              <li>Improve and optimize the service</li>
              <li>Detect, prevent, and address technical issues or security vulnerabilities</li>
              <li>Comply with legal obligations and enforce our Terms of Service</li>
            </ul>
          </section>

          <section>
            <h2 class="text-2xl font-semibold text-gray-800 mb-3">4. Data Storage and Security</h2>
            <h3 class="text-xl font-semibold text-gray-800 mb-2 mt-4">4.1 Data Location</h3>
            <p class="text-gray-700 leading-relaxed">
              Your data is stored on Cloudflare's infrastructure in Singapore. Generated documents and templates are stored
              in Cloudflare R2 object storage with encryption at rest.
            </p>

            <h3 class="text-xl font-semibold text-gray-800 mb-2 mt-4">4.2 Security Measures</h3>
            <ul class="list-disc pl-6 space-y-2 text-gray-700">
              <li>Encryption in transit using TLS 1.3</li>
              <li>Encryption at rest for all stored files</li>
              <li>Access controls and authentication via Lark SDK</li>
              <li>Regular security monitoring and vulnerability assessments</li>
              <li>Automated backups of critical data</li>
            </ul>

            <h3 class="text-xl font-semibold text-gray-800 mb-2 mt-4">4.3 Data Retention</h3>
            <ul class="list-disc pl-6 space-y-2 text-gray-700">
              <li><strong>Templates:</strong> Deleted based on your plan's retention period (60-90 days after last usage)</li>
              <li><strong>Generated Documents:</strong> Deleted based on your plan's retention period (7-14 days after generation)</li>
              <li><strong>Account Data:</strong> Retained during service term plus 90 days after termination</li>
            </ul>
          </section>

          <section>
            <h2 class="text-2xl font-semibold text-gray-800 mb-3">5. Information Sharing and Disclosure</h2>
            <p class="text-gray-700 mb-3">We do not sell your personal information. We may share your information in the following circumstances:</p>
            <ul class="list-disc pl-6 space-y-2 text-gray-700">
              <li><strong>Service Providers:</strong> Cloudflare (infrastructure), Lark/Feishu (data integration)</li>
              <li><strong>Authorized Resellers:</strong> If you subscribe through a reseller, we share subscription and usage data with them</li>
              <li><strong>Legal Requirements:</strong> When required by law, court order, or government request</li>
              <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets</li>
            </ul>
          </section>

          <section>
            <h2 class="text-2xl font-semibold text-gray-800 mb-3">6. Your Rights</h2>
            <p class="text-gray-700 mb-3">You have the right to:</p>
            <ul class="list-disc pl-6 space-y-2 text-gray-700">
              <li><strong>Access:</strong> Request a copy of your personal data</li>
              <li><strong>Correction:</strong> Request correction of inaccurate data</li>
              <li><strong>Deletion:</strong> Request deletion of your data</li>
              <li><strong>Export:</strong> Download your templates and generated documents</li>
              <li><strong>Objection:</strong> Object to certain processing activities</li>
            </ul>
            <p class="text-gray-700 mt-3">
              To exercise these rights, contact us at <a href="mailto:christophe@ksi-digital.com" class="text-blue-600 hover:text-blue-800">christophe@ksi-digital.com</a>
            </p>
          </section>

          <section>
            <h2 class="text-2xl font-semibold text-gray-800 mb-3">7. Data Breach Notification</h2>
            <p class="text-gray-700 leading-relaxed">
              In the event of a data breach affecting your information, we will notify you within 48 hours of discovery,
              including details about the breach, affected data, and steps we are taking to address it.
            </p>
          </section>

          <section>
            <h2 class="text-2xl font-semibold text-gray-800 mb-3">8. Children's Privacy</h2>
            <p class="text-gray-700 leading-relaxed">
              AutomaDoc Pro is not intended for use by individuals under the age of 18. We do not knowingly collect
              personal information from children.
            </p>
          </section>

          <section>
            <h2 class="text-2xl font-semibold text-gray-800 mb-3">9. International Data Transfers</h2>
            <p class="text-gray-700 leading-relaxed">
              Your data is stored in Singapore. By using AutomaDoc Pro, you consent to the transfer and processing
              of your data in Singapore.
            </p>
          </section>

          <section>
            <h2 class="text-2xl font-semibold text-gray-800 mb-3">10. Changes to This Policy</h2>
            <p class="text-gray-700 leading-relaxed">
              We may update this Privacy Policy from time to time. We will notify you of material changes by email
              or through the service at least 30 days before the changes take effect.
            </p>
          </section>

          <section>
            <h2 class="text-2xl font-semibold text-gray-800 mb-3">11. Contact Us</h2>
            <p class="text-gray-700 leading-relaxed">
              If you have questions about this Privacy Policy or our data practices, please contact us:
            </p>
            <div class="bg-gray-50 border border-gray-200 rounded-lg p-4 mt-3">
              <p class="text-gray-800 font-semibold mb-2">PT Karunia Solusi Informatika</p>
              <p class="text-gray-700">Jl. Margasatwa Raya No. 17, Pondok Labu</p>
              <p class="text-gray-700">Jakarta Selatan 12450, Indonesia</p>
              <p class="text-gray-700 mt-2">Email: <a href="mailto:christophe@ksi-digital.com" class="text-blue-600 hover:text-blue-800">christophe@ksi-digital.com</a></p>
              <p class="text-gray-700">Website: <a href="https://www.ksi-digital.com" target="_blank" class="text-blue-600 hover:text-blue-800">www.ksi-digital.com</a></p>
            </div>
          </section>
        </div>
    </div>
  </PublicLayout>
</template>
